import {
  action,
  computed,
  makeObservable,
  observable,
  reaction,
  runInAction,
} from 'mobx'

import SortOrder from '~/client/src/shared/enums/SortOrder'
import Localization from '~/client/src/shared/localization/LocalizationManager'
import { LogisticsDataKeys } from '~/client/src/shared/types/LogisticsDataKeys'

import LogisticsListStore from '../../../LogisticsList/LogisticsList.store'

type ISortingOption = {
  id: string
  caption: string
}

export default class LogisticsSortingOptionsStore {
  @observable public isShown: boolean = false
  @observable public selectedSortingOptionId: string = null
  @observable public selectedSortOrder: SortOrder = SortOrder.DEFAULT
  private sortStateDisposer: () => void = null
  private readonly uiOptionIdToStoreColumnKey: Map<string, string>
  private readonly storeColumnKeyToUiOptionId: Map<string, string>

  public constructor(
    private readonly logisticsListStore: LogisticsListStore,
    columnKeyMap?: { [uiOptionId: string]: string },
  ) {
    makeObservable(this)

    const forward = new Map<string, string>()
    const backward = new Map<string, string>()

    this.sortingOptions.forEach(opt => {
      forward.set(opt.id, opt.id)
      backward.set(opt.id, opt.id)
    })

    if (columnKeyMap) {
      Object.keys(columnKeyMap).forEach(uiId => {
        const storeKey = columnKeyMap[uiId]
        forward.set(uiId, storeKey)
        backward.set(storeKey, uiId)
      })
    }

    this.uiOptionIdToStoreColumnKey = forward
    this.storeColumnKeyToUiOptionId = backward
  }

  public get sortingOptions(): ISortingOption[] {
    return [
      {
        id: LogisticsDataKeys.LOGISTIC_NAME,
        caption: Localization.translator.name,
      },
      {
        id: LogisticsDataKeys.LOGISTIC_STATUS,
        caption: Localization.translator.status,
      },
      {
        id: LogisticsDataKeys.LOGISTIC_LOCATION,
        caption: Localization.translator.location,
      },
      {
        id: LogisticsDataKeys.LOGISTIC_COMPANY,
        caption: Localization.translator.company,
      },
      {
        id: LogisticsDataKeys.LOGISTIC_DATE,
        caption: Localization.translator.date,
      },
      {
        id: LogisticsDataKeys.LOGISTIC_RESPONSIBLE,
        caption: Localization.translator.responsibleUser,
      },
    ]
  }

  public get sortByCaption(): string {
    const option = this.currentSortingOption
    return option?.caption || Localization.translator.sortBy
  }

  @computed
  public get currentSortingOption(): ISortingOption {
    return this.sortingOptions.find(
      option => option.id === this.selectedSortingOptionId,
    )
  }

  public get sortByIcon(): string {
    const option = this.currentSortingOption
    if (!option) {
      return ''
    }

    return this.selectedSortOrder === SortOrder.ASC ? '↓' : '↑'
  }

  @action.bound
  public toggleSortingOptions() {
    this.isShown = !this.isShown
  }

  @action.bound
  public hideSortingOptions() {
    this.isShown = false
  }

  @action.bound
  public selectSortingOption(optionId: string) {
    if (this.selectedSortingOptionId === optionId) {
      this.selectedSortOrder =
        this.selectedSortOrder === SortOrder.ASC
          ? SortOrder.DESC
          : SortOrder.ASC
    } else {
      this.selectedSortingOptionId = optionId
      this.selectedSortOrder = SortOrder.DESC
    }

    const targetColumnKey =
      this.uiOptionIdToStoreColumnKey.get(optionId) || optionId
    this.logisticsListStore.handleColumnSort(targetColumnKey)
    this.hideSortingOptions()
  }

  @action.bound
  public initializeFromStore() {
    const { sortState } = this.logisticsListStore
    this.selectedSortingOptionId =
      this.storeColumnKeyToUiOptionId.get(sortState.columnKey) ||
      sortState.columnKey
    this.selectedSortOrder = sortState.order

    if (!this.sortStateDisposer) {
      this.sortStateDisposer = reaction(
        () => {
          const { columnKey, order } = this.logisticsListStore.sortState
          return { columnKey, order }
        },
        ({ columnKey, order }) => {
          runInAction(() => {
            this.selectedSortingOptionId =
              this.storeColumnKeyToUiOptionId.get(columnKey) || columnKey
            this.selectedSortOrder = order
          })
        },
      )
    }
  }

  public dispose() {
    if (this.sortStateDisposer) {
      this.sortStateDisposer()
      this.sortStateDisposer = null
    }
  }
}
